import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { Subscription } from 'rxjs';
import { HttpServiceService } from '../services/http-service.service';
import { NgxToastrService } from '../_services/ngx-toastr/ngx-toastr.service';
import { BreadcrumbComponent } from '../elements/breadcrumb/breadcrumb.component';
import { USER_PERMISSIONS } from '../app.constants';
import { LocalStorageService } from '../services/local-storage.service';

interface Item {
  id: number;
  item_name: string;
  item_icon: string | null;
  is_active: boolean;
}

@Component({
  selector: 'app-items',
  standalone: true,
  imports: [ReactiveFormsModule, NgFor, NgIf, NgClass, BreadcrumbComponent],
  templateUrl: './items.component.html',
  styleUrl: './items.component.css',
})
export class ItemsComponent implements OnInit, OnDestroy {
  breadcrumbList = {
    breadcrumb_path: 'Dashboard',
    currentURL: 'Items',
    breadcrumb_url: '/admin',
  };

  items: Item[] = [];
  searchQuery = '';
  isLoading = false;

  showModal = false;
  isEditMode = false;
  editingId: number | null = null;
  itemForm!: FormGroup;

  private httpService = inject(HttpServiceService);
  private alertService = inject(NgxToastrService);
  private fb = inject(FormBuilder);
  private localService = inject(LocalStorageService);

  private userPermissions: string[] = [];
  canAdd = false;
  canEdit = false;
  canDelete = false;

  private listSub?: Subscription;
  private saveSub?: Subscription;
  private deleteSub?: Subscription;

  ngOnInit(): void {
    this.userPermissions = this.localService.listUserPermissions();
    this.canAdd = this.userPermissions.includes(USER_PERMISSIONS.ITEMS.ADD_ITEM);
    this.canEdit = this.userPermissions.includes(USER_PERMISSIONS.ITEMS.EDIT_ITEM);
    this.canDelete = this.userPermissions.includes(USER_PERMISSIONS.ITEMS.DELETE_ITEM);
    this.initForm();
    this.loadItems();
  }

  initForm(): void {
    this.itemForm = this.fb.group({
      item_name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      item_icon: ['', Validators.maxLength(100)],
      is_active: [true],
    });
  }

  loadItems(): void {
    this.isLoading = true;
    this.listSub = this.httpService.getAdminItems(this.searchQuery || undefined).subscribe({
      next: (res) => {
        this.items = res.data ?? [];
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  onSearch(event: Event): void {
    this.searchQuery = (event.target as HTMLInputElement).value.trim();
    this.loadItems();
  }

  openCreateModal(): void {
    this.isEditMode = false;
    this.editingId = null;
    this.itemForm.reset({ item_name: '', item_icon: '', is_active: true });
    this.showModal = true;
  }

  openEditModal(item: Item): void {
    this.isEditMode = true;
    this.editingId = item.id;
    this.itemForm.patchValue({
      item_name: item.item_name,
      item_icon: item.item_icon ?? '',
      is_active: item.is_active,
    });
    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
    this.itemForm.reset({ item_name: '', item_icon: '', is_active: true });
  }

  saveItem(): void {
    if (this.itemForm.invalid) {
      this.itemForm.markAllAsTouched();
      return;
    }
    const payload = this.itemForm.value;

    if (this.isEditMode && this.editingId !== null) {
      this.saveSub = this.httpService.updateAdminItem(this.editingId, payload).subscribe({
        next: (res) => {
          this.alertService.success(res.message ?? 'Item updated.', 'toast-top-right');
          this.closeModal();
          this.loadItems();
        },
      });
    } else {
      this.saveSub = this.httpService.createAdminItem(payload).subscribe({
        next: (res) => {
          this.alertService.success(res.message ?? 'Item created.', 'toast-top-right');
          this.closeModal();
          this.loadItems();
        },
      });
    }
  }

  deleteItem(item: Item): void {
    if (!confirm(`Delete item "${item.item_name}"? This cannot be undone.`)) return;
    this.deleteSub = this.httpService.deleteAdminItem(item.id).subscribe({
      next: (res) => {
        this.alertService.success(res.message ?? 'Item deleted.', 'toast-top-right');
        this.loadItems();
      },
    });
  }

  ngOnDestroy(): void {
    this.listSub?.unsubscribe();
    this.saveSub?.unsubscribe();
    this.deleteSub?.unsubscribe();
  }
}
